/*!

=========================================================
* Argon Dashboard React - v1.2.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/

// reactstrap components
import { DateTime } from "luxon";
import React from "react";
import { Col, Container, Row, Card, CardBody, CardTitle, Button } from "reactstrap";
import { ExecutionStatus, ExecutionType, QuestionType } from "../../types";
// import { secondsToTimeString } from "../../utils/secondsToTimeString";
import { toTitleCase } from "../../utils/toTitleCase";
import { Description, Schedule, LiveHelp, AccountCircle, CloudDownload } from "@material-ui/icons";

const ExecutionInfoHeader: React.FC<{
  executionInfo?: ExecutionType;
  isLoading?: boolean;
  error?: boolean;
  questions?: QuestionType[];
}> = ({ executionInfo, isLoading, error, questions }) => {
  let content: JSX.Element | null = null
  if (isLoading) {
    content = (
      <h2 className="display-2 text-white">Loading...</h2>
    )
  } else if (error || !executionInfo) {
    content = (
      <>
        <h2 className="display-2 text-danger">Error!</h2>
        <p className="text-white mt-0">Cannot get this execution info</p>
      </>
    )
  } else {
    content = (
      <>
        <h2 className="display-3 text-white">
          {toTitleCase(`Execution ${executionInfo.id}`)}

          <Button
            color="primary"
            size="sm"
            className='ml-2'
            disabled={questions?.length === 0}
            style={{ background: "transparent", border: "none" }}
            onClick={() => {
              // TODO: have token appended in react query hook (data can be transformed using `select` option)
              window.open(`${process.env.REACT_APP_API_BASE_URL}/execution/export/${executionInfo?.id}?token=${localStorage.getItem('token')}`)
            }}
          >
            <CloudDownload />
          </Button>
        </h2>
        <Row>
          <Col lg="6" xl="3">
            <Card className="card-stats mb-4 mb-xl-0">
              <CardBody>
                <Row>
                  <div className="col">
                    <CardTitle
                      tag="h5"
                      className="text-uppercase text-muted mb-0"
                    >
                      Executed at
                    </CardTitle>
                    <span className="h5 font-weight-bold mb-0">
                      {executionInfo.createdAt && <span>{DateTime.fromJSDate(new Date(executionInfo.createdAt)).toRelative()}</span>}
                    </span>
                  </div>
                  <Col className="col-auto">
                    <div className="icon icon-shape bg-danger text-white rounded-circle shadow">
                      <Schedule />
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
          <Col lg="6" xl="3">
            <Card className="card-stats mb-4 mb-xl-0">
              <CardBody>
                <Row>
                  <div className="col">
                    <CardTitle
                      tag="h5"
                      className="text-uppercase text-muted mb-0"
                    >
                      Input Type
                    </CardTitle>
                    <span className="h5 font-weight-bold mb-0">
                      {executionInfo?.inputType}
                    </span>
                  </div>
                  <Col className="col-auto">
                    <div className="icon icon-shape bg-danger text-white rounded-circle shadow">
                      <Description />
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
          <Col lg="6" xl="3">
            <Card className="card-stats mb-4 mb-xl-0">
              <CardBody>
                <Row>
                  <div className="col">
                    <CardTitle
                      tag="h5"
                      className="text-uppercase text-muted mb-0"
                    >
                      Executed By
                    </CardTitle>
                    <span className="h5 font-weight-bold mb-0">
                      {executionInfo?.user?.name}
                    </span>
                  </div>
                  <Col className="col-auto">
                    <div className="icon icon-shape bg-danger text-white rounded-circle shadow">
                      <AccountCircle />
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
          <Col lg="6" xl="3">
            <Card className="card-stats mb-4 mb-xl-0">
              <CardBody>
                <Row>
                  <div className="col">
                    <CardTitle
                      tag="h5"
                      className="text-uppercase text-muted mb-0"
                    >
                      Questions
                    </CardTitle>
                    <span className="h5 font-weight-bold mb-0">
                      {questions?.length || 0}
                    </span>
                  </div>
                  <Col className="col-auto">
                    <div className="icon icon-shape bg-danger text-white rounded-circle shadow">
                      <LiveHelp />
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>
        {
          executionInfo.status === ExecutionStatus.error &&
          <p className="text-danger mt-0">Execution is in error state!</p>
        }
      </>
    )
  }

  return (
    <>
      <div
        className="header pb-8 pt-5 pt-lg-8 d-flex align-items-center"
        style={{
          minHeight: "450px",
          backgroundColor: '#2D315C',
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundImage: "url(/background-header-infinity.jpg)"
        }}
      >
        {/* Mask */}
        <span className="mask bg-gradient-default opacity-8" />
        <Container className="align-items-center" fluid>
          <Row>
            <Col>
              {content}
            </Col>
          </Row>

        </Container>
      </div>
    </>
  );
};

export default ExecutionInfoHeader;
